import type { AdminAppealPenaltyType } from '@carcheck/shared';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import type { ReactElement } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { ResolveFormValues } from '../../_lib/resolveAppealSchema';

/**
 * Conditional inspector-penalty inputs for the `inspector_penalty` outcome
 * (AUT-130, architect 🟠 #1). Penalty-type `<Select>` over the shared
 * `ADMIN_APPEAL_PENALTY_TYPES` const (passed in — never hand-typed); the
 * days field (int 1-365) shows ONLY for `suspension` (warning / ban have
 * no duration). `inspectorPenaltyType` is required by the Zod refine when
 * this outcome is chosen.
 */
export interface PenaltyFieldsProps {
  control: Control<ResolveFormValues>;
  loading: boolean;
  penaltyTypes: readonly AdminAppealPenaltyType[];
  /** True when the selected penalty type is `suspension` (days field shown). */
  showDays: boolean;
}

export const PenaltyFields = ({
  control,
  loading,
  penaltyTypes,
  showDays,
}: PenaltyFieldsProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', gap: 1.5 }}>
      <Controller
        name="inspectorPenaltyType"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            select
            fullWidth
            size="small"
            required
            disabled={loading}
            label={t('appeals.resolveDialog.penaltyTypeLabel')}
            error={Boolean(fieldState.error)}
            helperText={fieldState.error ? t(fieldState.error.message ?? '') : ' '}
          >
            {penaltyTypes.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`appeals.resolveDialog.penaltyType.${value}`)}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
      {showDays ? (
        <Controller
          name="inspectorPenaltyDays"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              size="small"
              inputMode="numeric"
              required
              disabled={loading}
              label={t('appeals.resolveDialog.penaltyDaysLabel')}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {t('appeals.resolveDialog.daysUnit')}
                    </InputAdornment>
                  ),
                },
              }}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error ? t(fieldState.error.message ?? '') : ' '}
            />
          )}
        />
      ) : null}
    </Box>
  );
};
